import { useState, useEffect } from "react";
import {
  Users,
  Shield,
  User,
  Loader2,
  AlertCircle,
  Mail,
  AlertTriangle,
  CheckCircle,
  Plus,
  Trash2,
  Pencil,
  Globe,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  api,
  AdminUser,
  UserRole,
  EmailAllowlistRule,
} from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

type Tab = "users" | "rules";

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("users");

  // Users state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [activatingUserId, setActivatingUserId] = useState<string | null>(null);
  const [togglingEditUserId, setTogglingEditUserId] = useState<string | null>(null);

  // Allowlist state
  const [rules, setRules] = useState<EmailAllowlistRule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(true);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [showAddRuleForm, setShowAddRuleForm] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [ruleForm, setRuleForm] = useState({
    type: "domain" as "domain" | "email",
    value: "",
    max_invites: 3,
  });
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
    loadRules();
  }, []);

  // ==================== Users ====================

  const loadUsers = async () => {
    try {
      setUsersError(null);
      const { users: userList } = await api.listUsers();
      setUsers(userList);
    } catch (err) {
      console.error("Failed to load users:", err);
      setUsersError(
        err instanceof Error ? err.message : "Failed to load users"
      );
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    if (userId === currentUser?.id) return;

    setUpdatingUserId(userId);
    try {
      const { user: updatedUser } = await api.updateUserRole(userId, newRole);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? updatedUser : u))
      );
    } catch (err) {
      console.error("Failed to update user role:", err);
      setUsersError(
        err instanceof Error ? err.message : "Failed to update role"
      );
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleActivateUser = async (userId: string) => {
    setActivatingUserId(userId);
    try {
      const { user: updatedUser } = await api.activateUser(userId);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? updatedUser : u))
      );
    } catch (err) {
      console.error("Failed to activate user:", err);
      setUsersError(
        err instanceof Error ? err.message : "Failed to activate user"
      );
    } finally {
      setActivatingUserId(null);
    }
  };

  const handleToggleCanEditReports = async (userId: string, canEdit: boolean) => {
    setTogglingEditUserId(userId);
    try {
      const { user: updatedUser } = await api.updateCanEditReports(userId, canEdit);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? updatedUser : u))
      );
    } catch (err) {
      console.error("Failed to toggle can_edit_reports:", err);
      setUsersError(
        err instanceof Error ? err.message : "Failed to update edit permission"
      );
    } finally {
      setTogglingEditUserId(null);
    }
  };

  // ==================== Allowlist ====================

  const loadRules = async () => {
    try {
      setRulesError(null);
      const { rules: list } = await api.listEmailAllowlist();
      setRules(list);
    } catch (err) {
      console.error("Failed to load allowlist:", err);
      setRulesError(
        err instanceof Error ? err.message : "Failed to load email rules"
      );
    } finally {
      setIsLoadingRules(false);
    }
  };

  const handleAddRule = async () => {
    if (!ruleForm.value.trim()) return;

    try {
      setRulesError(null);
      const { rule } = await api.createEmailAllowlistRule(
        ruleForm.type,
        ruleForm.value.trim(),
        ruleForm.max_invites
      );
      setRules((prev) => [{ ...rule, used_slots: 0 }, ...prev]);
      setShowAddRuleForm(false);
      setRuleForm({ type: "domain", value: "", max_invites: 3 });
    } catch (err) {
      console.error("Failed to create rule:", err);
      setRulesError(
        err instanceof Error ? err.message : "Failed to create rule"
      );
    }
  };

  const handleUpdateRule = async (id: string) => {
    if (!ruleForm.value.trim()) return;

    try {
      setRulesError(null);
      await api.updateEmailAllowlistRule(id, {
        value: ruleForm.value.trim(),
        max_invites: ruleForm.max_invites,
      });
      setEditingRuleId(null);
      setRuleForm({ type: "domain", value: "", max_invites: 3 });
      loadRules();
    } catch (err) {
      console.error("Failed to update rule:", err);
      setRulesError(
        err instanceof Error ? err.message : "Failed to update rule"
      );
    }
  };

  const handleDeleteRule = async (id: string) => {
    setDeletingRuleId(id);
    try {
      await api.deleteEmailAllowlistRule(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error("Failed to delete rule:", err);
      setRulesError(
        err instanceof Error ? err.message : "Failed to delete rule"
      );
    } finally {
      setDeletingRuleId(null);
    }
  };

  const startEditRule = (rule: EmailAllowlistRule) => {
    setEditingRuleId(rule.id);
    setRuleForm({
      type: rule.type,
      value: rule.value,
      max_invites: rule.max_invites,
    });
  };

  // ==================== Helpers ====================

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getProviderLabel = (provider: string) => {
    const labels: Record<string, string> = {
      local: "Email",
      discord: "Discord",
      feedforward: "Feedforward",
      google: "Google",
      github: "GitHub",
    };
    return labels[provider] || provider;
  };

  const pendingUsersCount = users.filter((u) => u.status === "pending").length;

  const isLoading =
    activeTab === "users"
      ? isLoadingUsers
      : isLoadingRules;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const error =
    activeTab === "users"
      ? usersError
      : rulesError;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Users className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            User Management
          </h1>
        </div>
        <p className="text-muted-foreground">
          Manage user accounts, registrations, and email authorization rules.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab("users")}
          className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
            activeTab === "users"
              ? "text-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <span className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Users
            {pendingUsersCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-amber-500 text-white rounded-full">
                {pendingUsersCount}
              </span>
            )}
          </span>
          {activeTab === "users" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
        <button
          onClick={() => setActiveTab("rules")}
          className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
            activeTab === "rules"
              ? "text-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <span className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Email Rules
          </span>
          {activeTab === "rules" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === "users" && (
        <UsersTab
          users={users}
          currentUserId={currentUser?.id}
          currentUserCanEdit={currentUser?.can_edit_reports === true}
          updatingUserId={updatingUserId}
          activatingUserId={activatingUserId}
          togglingEditUserId={togglingEditUserId}
          onRoleChange={handleRoleChange}
          onActivateUser={handleActivateUser}
          onToggleCanEditReports={handleToggleCanEditReports}
          formatDate={formatDate}
          getProviderLabel={getProviderLabel}
        />
      )}

      {activeTab === "rules" && (
        <EmailRulesTab
          rules={rules}
          showAddForm={showAddRuleForm}
          editingRuleId={editingRuleId}
          ruleForm={ruleForm}
          deletingRuleId={deletingRuleId}
          onToggleAddForm={() => {
            setShowAddRuleForm(!showAddRuleForm);
            setEditingRuleId(null);
            setRuleForm({ type: "domain", value: "", max_invites: 3 });
          }}
          onRuleFormChange={setRuleForm}
          onAddRule={handleAddRule}
          onStartEdit={startEditRule}
          onCancelEdit={() => {
            setEditingRuleId(null);
            setRuleForm({ type: "domain", value: "", max_invites: 3 });
          }}
          onUpdateRule={handleUpdateRule}
          onDeleteRule={handleDeleteRule}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

// ==================== Users Tab ====================

function UsersTab({
  users,
  currentUserId,
  currentUserCanEdit,
  updatingUserId,
  activatingUserId,
  togglingEditUserId,
  onRoleChange,
  onActivateUser,
  onToggleCanEditReports,
  formatDate,
  getProviderLabel,
}: {
  users: AdminUser[];
  currentUserId?: string;
  currentUserCanEdit: boolean;
  updatingUserId: string | null;
  activatingUserId: string | null;
  togglingEditUserId: string | null;
  onRoleChange: (userId: string, role: UserRole) => void;
  onActivateUser: (userId: string) => void;
  onToggleCanEditReports: (userId: string, canEdit: boolean) => void;
  formatDate: (d: string) => string;
  getProviderLabel: (p: string) => string;
}) {
  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="p-4 bg-card border border-border rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Total Users</p>
          <p className="text-2xl font-bold text-foreground">{users.length}</p>
        </div>
        <div className="p-4 bg-card border border-border rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Active</p>
          <p className="text-2xl font-bold text-primary">
            {users.filter((u) => (u.status ?? "active") === "active").length}
          </p>
        </div>
        <div className="p-4 bg-card border border-border rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Pending</p>
          <p className="text-2xl font-bold text-amber-500">
            {users.filter((u) => u.status === "pending").length}
          </p>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  User
                </th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Status
                </th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Sign-in Method
                </th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Role
                </th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Daily Limit
                </th>
                {currentUserCanEdit && (
                  <th className="text-center p-4 text-sm font-medium text-muted-foreground">
                    Can Edit
                  </th>
                )}
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Joined
                </th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className={`border-b border-border last:border-0 hover:bg-muted/20 transition-colors ${
                    user.status === "pending" ? "bg-amber-500/5" : ""
                  }`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.display_name || user.email}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-foreground">
                          {user.display_name || "No name"}
                          {user.id === currentUserId && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              (you)
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="p-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        (user.status ?? "active") === "active"
                          ? "bg-green-500/10 text-green-600"
                          : "bg-amber-500/10 text-amber-600"
                      }`}
                    >
                      {(user.status ?? "active") === "active"
                        ? "Active"
                        : "Pending"}
                    </span>
                  </td>

                  <td className="p-4">
                    {user.invited_by ? (
                      <span className="relative group/invite inline-flex items-center gap-1 text-sm text-muted-foreground cursor-default">
                        <Mail className="w-3.5 h-3.5" />
                        Email Invite
                        <span className="invisible group-hover/invite:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50">
                          <span className="block bg-white text-gray-700 text-xs font-medium rounded-lg px-3 py-2 shadow-lg border border-gray-200 whitespace-nowrap">
                            Invited by{" "}
                            {users.find((u) => u.id === user.invited_by)
                              ?.display_name || "Unknown user"}
                          </span>
                          <span className="block w-0 h-0 mx-auto border-x-[6px] border-x-transparent border-t-[6px] border-t-gray-200" />
                        </span>
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        {getProviderLabel(user.primary_provider)}
                      </span>
                    )}
                  </td>

                  <td className="p-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        user.role === "admin"
                          ? "bg-primary/10 text-primary"
                          : user.role === "disabled"
                          ? "bg-destructive/10 text-destructive"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {user.role === "admin" ? (
                        <Shield className="w-3 h-3" />
                      ) : (
                        <User className="w-3 h-3" />
                      )}
                      {user.role === "admin"
                        ? "Admin"
                        : user.role === "disabled"
                        ? "Disabled"
                        : "User"}
                    </span>
                  </td>

                  <td className="p-4">
                    {user.daily_usage ? (
                      user.role === "admin" ? (
                        <span className="text-sm text-muted-foreground">Unlimited</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-sm font-medium tabular-nums ${
                              user.daily_usage.used >= user.daily_usage.limit
                                ? "text-destructive"
                                : "text-foreground"
                            }`}
                          >
                            {user.daily_usage.used}/{user.daily_usage.limit}
                          </span>
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                user.daily_usage.used >= user.daily_usage.limit
                                  ? "bg-destructive"
                                  : "bg-primary"
                              }`}
                              style={{
                                width: `${Math.min(
                                  (user.daily_usage.used / user.daily_usage.limit) * 100,
                                  100
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                      )
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </td>

                  {currentUserCanEdit && (
                    <td className="p-4 text-center">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={user.can_edit_reports ?? false}
                          onChange={(e) =>
                            onToggleCanEditReports(user.id, e.target.checked)
                          }
                          disabled={togglingEditUserId === user.id}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary/50 disabled:opacity-50"
                        />
                        {togglingEditUserId === user.id && (
                          <Loader2 className="w-3 h-3 animate-spin text-primary ml-1" />
                        )}
                      </label>
                    </td>
                  )}

                  <td className="p-4">
                    <span className="relative group/joined inline-flex text-sm text-muted-foreground cursor-default">
                      {formatDate(user.created_at)}
                      {user.timezone && (
                        <span className="invisible group-hover/joined:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50">
                          <span className="block bg-white text-gray-700 text-xs font-medium rounded-lg px-3 py-2 shadow-lg border border-gray-200 whitespace-nowrap">
                            {user.timezone.replace(/_/g, " ")}
                          </span>
                          <span className="block w-0 h-0 mx-auto border-x-[6px] border-x-transparent border-t-[6px] border-t-gray-200" />
                        </span>
                      )}
                    </span>
                  </td>

                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {user.status === "pending" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onActivateUser(user.id)}
                          disabled={activatingUserId === user.id}
                          className="text-xs"
                        >
                          {activatingUserId === user.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3 h-3" />
                          )}
                          Activate
                        </Button>
                      )}
                      {user.id === currentUserId ? (
                        <span className="text-xs text-muted-foreground">
                          Cannot change own role
                        </span>
                      ) : (
                        <select
                          value={user.role}
                          onChange={(e) =>
                            onRoleChange(user.id, e.target.value as UserRole)
                          }
                          disabled={updatingUserId === user.id}
                          className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      )}
                      {updatingUserId === user.id && (
                        <Loader2 className="w-4 h-4 animate-spin text-primary ml-2 inline" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No users found</p>
          </div>
        )}
      </div>
    </>
  );
}

// ==================== Email Rules Tab ====================

function EmailRulesTab({
  rules,
  showAddForm,
  editingRuleId,
  ruleForm,
  deletingRuleId,
  onToggleAddForm,
  onRuleFormChange,
  onAddRule,
  onStartEdit,
  onCancelEdit,
  onUpdateRule,
  onDeleteRule,
  formatDate,
}: {
  rules: EmailAllowlistRule[];
  showAddForm: boolean;
  editingRuleId: string | null;
  ruleForm: { type: "domain" | "email"; value: string; max_invites: number };
  deletingRuleId: string | null;
  onToggleAddForm: () => void;
  onRuleFormChange: (form: {
    type: "domain" | "email";
    value: string;
    max_invites: number;
  }) => void;
  onAddRule: () => void;
  onStartEdit: (rule: EmailAllowlistRule) => void;
  onCancelEdit: () => void;
  onUpdateRule: (id: string) => void;
  onDeleteRule: (id: string) => void;
  formatDate: (d: string) => string;
}) {
  return (
    <>
      {/* Header with Add button */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground">
          Define which email domains and specific emails are authorized for
          registration.
        </p>
        <Button
          variant={showAddForm ? "outline" : "brand"}
          size="sm"
          onClick={onToggleAddForm}
        >
          {showAddForm ? (
            <>
              <X className="w-4 h-4" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Add Rule
            </>
          )}
        </Button>
      </div>

      {/* Add Rule Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-card border border-border rounded-lg">
          <h3 className="text-sm font-medium text-foreground mb-3">
            New Email Rule
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <select
              value={ruleForm.type}
              onChange={(e) => {
                const newType = e.target.value as "domain" | "email";
                onRuleFormChange({
                  ...ruleForm,
                  type: newType,
                  max_invites: newType === "email" ? 1 : ruleForm.max_invites,
                });
              }}
              className="px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="domain">Domain</option>
              <option value="email">Email</option>
            </select>
            <input
              type="text"
              value={ruleForm.value}
              onChange={(e) =>
                onRuleFormChange({ ...ruleForm, value: e.target.value })
              }
              placeholder={
                ruleForm.type === "domain"
                  ? "e.g., google.com"
                  : "e.g., user@company.com"
              }
              className="px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <input
              type="number"
              value={ruleForm.max_invites}
              onChange={(e) =>
                onRuleFormChange({
                  ...ruleForm,
                  max_invites: parseInt(e.target.value) || 1,
                })
              }
              min={1}
              disabled={ruleForm.type === "email"}
              placeholder="Max registrations"
              className={`px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 ${ruleForm.type === "email" ? "opacity-50 cursor-not-allowed" : ""}`}
            />
            <Button variant="brand" size="sm" onClick={onAddRule}>
              <Plus className="w-4 h-4" />
              Add
            </Button>
          </div>
        </div>
      )}

      {/* Rules Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Type
                </th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Value
                </th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Usage
                </th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Created
                </th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr
                  key={rule.id}
                  className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                >
                  <td className="p-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        rule.type === "domain"
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {rule.type === "domain" ? (
                        <Globe className="w-3 h-3" />
                      ) : (
                        <Mail className="w-3 h-3" />
                      )}
                      {rule.type === "domain" ? "Domain" : "Email"}
                    </span>
                  </td>

                  <td className="p-4">
                    {editingRuleId === rule.id ? (
                      <input
                        type="text"
                        value={ruleForm.value}
                        onChange={(e) =>
                          onRuleFormChange({
                            ...ruleForm,
                            value: e.target.value,
                          })
                        }
                        className="px-2 py-1 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 w-full max-w-[200px]"
                      />
                    ) : (
                      <span className="text-sm font-medium text-foreground">
                        {rule.value}
                      </span>
                    )}
                  </td>

                  <td className="p-4">
                    {editingRuleId === rule.id ? (
                      <input
                        type="number"
                        value={ruleForm.max_invites}
                        onChange={(e) =>
                          onRuleFormChange({
                            ...ruleForm,
                            max_invites: parseInt(e.target.value) || 1,
                          })
                        }
                        min={1}
                        disabled={ruleForm.type === "email"}
                        className={`px-2 py-1 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 w-20 ${ruleForm.type === "email" ? "opacity-50 cursor-not-allowed" : ""}`}
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-sm font-medium ${
                            rule.used_slots >= rule.max_invites
                              ? "text-amber-600"
                              : "text-foreground"
                          }`}
                        >
                          {rule.used_slots}/{rule.max_invites}
                        </span>
                        {/* Progress bar */}
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              rule.used_slots >= rule.max_invites
                                ? "bg-amber-500"
                                : "bg-primary"
                            }`}
                            style={{
                              width: `${Math.min(
                                (rule.used_slots / rule.max_invites) * 100,
                                100
                              )}%`,
                            }}
                          />
                        </div>
                        {rule.used_slots >= rule.max_invites && (
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                        )}
                      </div>
                    )}
                  </td>

                  <td className="p-4">
                    <span className="text-sm text-muted-foreground">
                      {formatDate(rule.created_at)}
                    </span>
                  </td>

                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {editingRuleId === rule.id ? (
                        <>
                          <Button
                            variant="brand"
                            size="sm"
                            onClick={() => onUpdateRule(rule.id)}
                            className="text-xs"
                          >
                            Save
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={onCancelEdit}
                            className="text-xs"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => onStartEdit(rule)}
                            className="p-1.5 rounded-md hover:bg-muted transition-colors"
                            title="Edit rule"
                          >
                            <Pencil className="w-4 h-4 text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => onDeleteRule(rule.id)}
                            disabled={deletingRuleId === rule.id}
                            className="p-1.5 rounded-md hover:bg-destructive/10 transition-colors disabled:opacity-50"
                            title="Delete rule"
                          >
                            {deletingRuleId === rule.id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-destructive" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-destructive" />
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rules.length === 0 && (
          <div className="p-8 text-center">
            <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground mb-2">No email rules defined</p>
            <p className="text-sm text-muted-foreground">
              Add rules to authorize specific domains or emails for registration.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
